import dayjs from 'dayjs'
import {
  EditFilled,
  DeleteOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  QrcodeOutlined,
} from '@ant-design/icons'
import { Button, IconButton, Tag } from '@/components/ui'

interface ScanPoint {
  id: number
  name: string
  kind: string
  start_at: string | null
  end_at: string | null
  allow_anytime_scans: boolean
  allow_multiple_scans: boolean
  agenda_session_id: number | null
  agenda_session_name: string | null
  scan_count: number
}

interface ScanPointCardProps {
  scanPoint: ScanPoint
  onEdit: (scanPoint: ScanPoint) => void
  onDelete: (id: number, name: string) => void
  onViewAttendees: (id: number) => void
}

export default function ScanPointCard({
  scanPoint,
  onEdit,
  onDelete,
  onViewAttendees,
}: ScanPointCardProps) {
  const getKindLabel = (kind: string) => {
    switch (kind) {
      // Legacy check-in points are ordinary custom points now.
      case 'event_check_in':
      case 'custom':
        return 'Custom'
      case 'session_link':
        return 'Session Link'
      default:
        return kind
    }
  }

  const getKindColor = (kind: string) => {
    switch (kind) {
      case 'event_check_in':
      case 'custom':
        return 'var(--color-ava-pink-02)'
      case 'session_link':
        return 'var(--color-ava-blue-05)'
      default:
        return 'default'
    }
  }

  const formatDateTime = (value: string | null) =>
    value ? dayjs(value).format('MMM D, h:mm A') : null

  const windowLabel = () => {
    if (scanPoint.allow_anytime_scans) return 'Scannable anytime'
    const start = formatDateTime(scanPoint.start_at)
    const end = formatDateTime(scanPoint.end_at)
    if (!start && !end) return 'No time window set'
    if (start && end) return `${start} - ${end}`
    if (start) return `Starts ${start}`
    return `Ends ${end}`
  }

  const scanModeLabel = scanPoint.allow_multiple_scans ? 'Multi-scan enabled' : 'Single scan only'
  const attendeeCountLabel = `View attendees${
    scanPoint.scan_count ? ` (${scanPoint.scan_count})` : ''
  }`

  return (
    <div className="relative overflow-hidden border border-slate-200 bg-white/80 p-4 shadow-sm">
      <div
        className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-indigo-500 to-cyan-500"
        aria-hidden
      />

      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
            <QrcodeOutlined />
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center">
              <h3 className="text-base font-semibold text-slate-900 mr-4">{scanPoint.name}</h3>
              <Tag
                color={getKindColor(scanPoint.kind)}
                style={{ fontSize: 12, padding: '2px 6px', lineHeight: '18px' }}
              >
                {getKindLabel(scanPoint.kind)}
              </Tag>

              <Tag
                color={scanPoint.allow_multiple_scans ? 'cyan' : 'blue'}
                style={{ fontSize: 12, padding: '2px 6px', lineHeight: '18px' }}
              >
                {scanModeLabel}
              </Tag>
              {scanPoint.allow_anytime_scans && (
                <Tag
                  color="green"
                  style={{ fontSize: 12, padding: '2px 6px', lineHeight: '18px' }}
                >
                  Anytime scans
                </Tag>
              )}
            </div>

            {scanPoint.agenda_session_name && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <LinkOutlined className="text-slate-400" />
                <span>{scanPoint.agenda_session_name}</span>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm text-slate-600">
              <ClockCircleOutlined className="text-slate-400" />
              <span>{windowLabel()}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end md:self-start">
          <Button variant="secondary" onClick={() => onViewAttendees(scanPoint.id)}>
            {attendeeCountLabel}
          </Button>
          <IconButton
            icon={<EditFilled />}
            onClick={() => onEdit(scanPoint)}
            ariaLabel="Edit scan point"
            title="Edit"
          />
          <IconButton
            icon={<DeleteOutlined />}
            onClick={() => onDelete(scanPoint.id, scanPoint.name)}
            ariaLabel="Delete scan point"
            title="Delete"
          />
        </div>
      </div>
    </div>
  )
}
